/**
 * SubtaskList component - displays and manages subtasks
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Plus, Trash2, CheckCircle2, Circle } from 'lucide-react';
import type { Subtask } from '@services/api';
import { toast } from '@components/ui/Toast';

interface SubtaskListProps {
  taskId: string;
  subtasks: Subtask[];
  onToggle: (taskId: string, subtaskId: string) => Promise<void>;
  onCreate: (taskId: string, title: string) => Promise<Subtask>;
  onDelete: (taskId: string, subtaskId: string) => Promise<void>;
}

export default function SubtaskList({
  taskId,
  subtasks,
  onToggle,
  onCreate,
  onDelete,
}: SubtaskListProps) {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const completedCount = subtasks.filter((s) => s.isCompleted).length;
  const progress = subtasks.length > 0 ? (completedCount / subtasks.length) * 100 : 0;

  const handleCreate = async () => {
    if (!newTitle.trim() || isCreating) return;

    setIsCreating(true);
    try {
      await onCreate(taskId, newTitle.trim());
      setNewTitle('');
      setIsAdding(false);
    } catch (error) {
      console.error('Failed to create subtask:', error);
      toast.error(t('task.failedCreateSubtask', 'Could not create subtask'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggle = async (subtaskId: string) => {
    try {
      await onToggle(taskId, subtaskId);
    } catch (error) {
      console.error('Failed to toggle subtask:', error);
      toast.error(t('task.failedUpdateSubtask', 'Could not update subtask'));
    }
  };

  const handleDelete = async (subtaskId: string) => {
    try {
      await onDelete(taskId, subtaskId);
    } catch (error) {
      console.error('Failed to delete subtask:', error);
      toast.error(t('task.failedDeleteSubtask'));
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
          <CheckCircle2 className="h-4 w-4" />
          {t('task.subtasksTitle', 'Subtasks')}
          {subtasks.length > 0 && (
            <span className="text-neutral-400">
              ({completedCount}/{subtasks.length})
            </span>
          )}
        </h3>
        {subtasks.length > 0 && <button
          onClick={() => setIsAdding(true)}
          className={clsx(
            'flex items-center gap-1 rounded px-2 py-1 text-xs',
            'text-neutral-600 hover:bg-neutral-100',
            'dark:text-neutral-400 dark:hover:bg-neutral-800'
          )}
        >
          <Plus className="h-3 w-3" />
          {t('common.add')}
        </button>}
      </div>

      {/* Progress bar */}
      {subtasks.length > 0 && (
        <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
          <div
            className="h-full rounded-full bg-green-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Subtasks list */}
      <div className="space-y-1">
        {[...subtasks]
          .sort((a, b) => a.position - b.position)
          .map((subtask) => (
            <SubtaskItem
              key={subtask.id}
              subtask={subtask}
              onToggle={() => handleToggle(subtask.id)}
              onDelete={() => handleDelete(subtask.id)}
            />
          ))}

        {/* Add new subtask input */}
        {isAdding && (
          <div className="flex items-center gap-2 rounded-lg bg-neutral-100 p-2 dark:bg-neutral-800">
            <Circle className="h-4 w-4 text-neutral-400" />
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleCreate();
                if (e.key === 'Escape') {
                  e.preventDefault(); e.stopPropagation();
                  setIsAdding(false);
                  setNewTitle('');
                }
              }}
              className={clsx(
                'flex-1 bg-transparent text-sm',
                'text-neutral-900 placeholder-neutral-400',
                'dark:text-white dark:placeholder-neutral-500',
                'focus:outline-none'
              )}
              placeholder={t('task.addSubtask', 'Add a subtask')}
              autoFocus
              disabled={isCreating}
            />
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || isCreating}
              className={clsx(
                'rounded px-2 py-1 text-xs font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {t('common.add')}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewTitle('');
              }}
              className="rounded p-1 text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700"
            >
              <span className="sr-only">{t('common.cancel')}</span>
              ×
            </button>
          </div>
        )}

        {/* Empty state */}
        {subtasks.length === 0 && !isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className={clsx(
              'flex w-full items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2.5',
              'border-neutral-300 text-neutral-500',
              'hover:border-neutral-400 hover:text-neutral-600',
              'dark:border-neutral-700 dark:hover:border-neutral-600'
            )}
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm">{t('task.addSubtask', 'Add a subtask')}</span>
          </button>
        )}
      </div>
    </div>
  );
}

interface SubtaskItemProps {
  subtask: Subtask;
  onToggle: () => void;
  onDelete: () => void;
}

function SubtaskItem({ subtask, onToggle, onDelete }: SubtaskItemProps) {
  const { t } = useTranslation();

  return (
    <div
      className={clsx(
        'group flex items-center gap-2 rounded-lg px-2 py-1.5',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800/50'
      )}
    >
      {/* Checkbox */}
      <button
        onClick={onToggle}
        role="checkbox"
        aria-checked={subtask.isCompleted}
        aria-label={subtask.title}
        className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        {subtask.isCompleted ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <Circle className="h-5 w-5 text-neutral-400 hover:text-neutral-500" />
        )}
      </button>

      {/* Title */}
      <span
        className={clsx(
          'flex-1 text-sm',
          subtask.isCompleted
            ? 'text-neutral-500 line-through dark:text-neutral-400'
            : 'text-neutral-900 dark:text-white'
        )}
      >
        {subtask.title}
      </span>

      {/* Delete button */}
      <button
        onClick={onDelete}
        aria-label={`${t('common.delete')}: ${subtask.title}`}
        className={clsx(
          'flex-shrink-0 rounded p-1',
          'text-neutral-400 hover:bg-red-100 hover:text-red-600',
          'dark:hover:bg-red-900/30 dark:hover:text-red-400',
          'transition-opacity opacity-100',
          '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100',
          'focus-visible:opacity-100 group-focus-within:opacity-100'
        )}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}
